import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFrame, useThree } from '@react-three/fiber';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onSetActiveFaceLandmark } from '../../../../store/actions';
import { getActiveSceneFaceLandmarkIdByFacelandmark } from '../../../../utils/pure/get';
import { IFaceLandmark } from '../../r3f-components/component-data-structure';
import { isObjectVisible } from './utils/isObjectVisible';
import { COLORS } from '@/utils';
const FaceLandmarkHotspot = ({ position, landmark }) => {
    const dispatch = useDispatch();
    const groupRef = useRef(null);
    const { scene } = useThree();
    const occludeRef = useRef(scene.getObjectByName('zappar-head-mesh'));
    const entityDragHotspotIsActive = useSelector((state) => state.userReducer.entityMenuDragActive);
    const activeLandmark = useSelector((state) => state.userReducer.activeFaceLandmark);
    const hoveredDropdownLandmark = useSelector((state) => state.userReducer.hoveredDropdownFacelandmark);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const showFaceLandmarks = useSelector((state) => state.userReducer.showFaceLandmarks);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const [selectedEntityId] = useSelector((state) => state.userReducer.selectedEntityIds || []);
    const [isOccluded, setIsOccluded] = useState(false);
    const anchorPlaceholderRef = useRef(null);
    const isOriginLandmark = landmark === IFaceLandmark.origin;
    const isVisible = (!isOccluded || isOriginLandmark) && !isScreenRelativeMode && (landmark === activeLandmark || showFaceLandmarks);
    const isSelected = landmark === activeLandmark || landmark === hoveredDropdownLandmark;
    useFrame(({ camera, raycaster }) => {
        if (!groupRef.current)
            return;
        groupRef.current.lookAt(camera.position);
        if (occludeRef.current && !isOriginLandmark) {
            // camera.updateMatrixWorld()
            // groupRef.current.updateWorldMatrix(true, false)
            const occluded = !isObjectVisible(groupRef.current, camera, raycaster, [occludeRef.current]);
            if (occluded !== isOccluded)
                setIsOccluded(occluded);
        }
    });
    useEffect(() => {
        if (isScreenRelativeMode)
            return;
        if (!selectedEntityId) {
            dispatch(onSetActiveFaceLandmark(null));
            return;
        }
        const id = getActiveSceneFaceLandmarkIdByFacelandmark(landmark, activeSceneId, componentsById);
        if (!id)
            return;
        const { children: faceLandmarkChildren } = componentsById[id];
        if (faceLandmarkChildren.includes(selectedEntityId)) {
            dispatch(onSetActiveFaceLandmark(landmark));
            return;
        }
    }, [selectedEntityId, componentsById, landmark, isScreenRelativeMode]);
    const pointerMoveHandler = (e) => {
        e.stopPropagation();
        if (landmark === activeLandmark)
            return;
        let distance = Infinity;
        let closestLandmark = undefined;
        e.intersections.forEach(intersection => {
            if (intersection.object.name.includes('landmark-')) {
                const distanceToCenter = intersection.point.distanceTo(intersection.object.parent.position);
                if (distanceToCenter < distance) {
                    distance = distanceToCenter;
                    closestLandmark = intersection.object.name.split('-')[1];
                }
            }
        });
        if (closestLandmark !== landmark)
            return;
        dispatch(onSetActiveFaceLandmark(landmark));
    };
    const scaleFactor = isOriginLandmark ? 0.8 : 0.5;
    return (_jsxs("group", { scale: 1, ref: groupRef, name: landmark, position: position, visible: isVisible, children: [_jsxs("mesh", { renderOrder: 9999, ref: anchorPlaceholderRef, children: [_jsx("circleGeometry", { args: [0.06 * scaleFactor, 20 * scaleFactor] }), _jsx("meshBasicMaterial", { color: isSelected ? COLORS.nebula : '#B4BBC3', transparent: true, opacity: isOriginLandmark ? 0.5 : 1, depthWrite: false, depthTest: false })] }), _jsxs("mesh", { renderOrder: 9999, children: [_jsx("ringGeometry", { args: [0.06 * scaleFactor, 0.075 * scaleFactor, 20, 20] }), _jsx("meshBasicMaterial", { color: 'white', transparent: true, opacity: isOriginLandmark ? 0.5 : 1, depthWrite: false, depthTest: false })] }), _jsxs("mesh", { renderOrder: 2, name: `landmark-${landmark}`, onPointerMove: isVisible && entityDragHotspotIsActive ? pointerMoveHandler : undefined, children: [_jsx("planeGeometry", { args: [0.6, 0.6] }), _jsx("meshBasicMaterial", { visible: false, color: 'black' })] })] }));
};
export default memo(FaceLandmarkHotspot);
